/**
 * Enhanced AdminClassPage using the new component system
 * Demonstrates class management with enhanced table features
 */

import {
  Badge,
  Box,
  Button,
  ButtonGroup,
  Stack,
  Input,
  InputGroup,
  InputLeftElement,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import React, { useEffect, useMemo, useState } from 'react';
import {
  FiBookOpen,
  FiEdit,
  FiSearch,
  FiTrash2,
  FiUsers,
} from 'react-icons/fi';
import { useNavigate, useParams } from 'react-router-dom';

// New component system imports
import { HStack, VStack } from '@chakra-ui/react';
import { ConfirmModal, CrudPageLayout, TableColumn } from '@grcs/components';

// Existing hooks and types
import {
  useClassOperations,
  useSemesterOperations,
  useUserOperations,
} from '@grcs/hook/useDatabase';
import { useEnhancedTableData } from '@grcs/hook/useEnhancedTableData';
import { ClassProfile, ClassTeacherProfile } from '@grcs/types/DataTypes';
import { TABLE_NAMES } from '@grcs/utils/database/types';
import { AdminSummary } from '../shared';

// Enhanced interface with optional deleted property
interface EnhancedClassTeacherProfile extends ClassTeacherProfile {
  deleted?: boolean;
}

export const AdminClassPageEnhanced: React.FC = () => {
  const semesterOps = useSemesterOperations();
  const userOps = useUserOperations();
  const classOps = useClassOperations();
  const { year } = useParams();
  const navigate = useNavigate();

  const [currentYear, setCurrentYear] = useState('');
  const [search, setSearch] = useState('');
  const [assignmentFilter, setAssignmentFilter] = useState<
    'all' | 'unassigned'
  >('all');
  const [isEnriching, setIsEnriching] = useState(true);
  const muted = useColorModeValue('gray.600', 'gray.300');
  const [classes, setClasses] = useState<EnhancedClassTeacherProfile[]>([]);
  const [deleteId, setDeleteId] = useState('');
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    data: classTableData,
    error: classTableDataError,
    isLoading: isTableLoading,
  } = useEnhancedTableData(TABLE_NAMES.CLASS, 'class_token');

  // Get current semester year with comprehensive error handling
  useEffect(() => {
    const fetchCurrentYear = async () => {
      try {
        console.log('🗓️ Loading current semester for AdminClass page...');
        const { data, error: semesterError } =
          await semesterOps.getCurrentSemesterYear();

        if (semesterError) {
          console.error('❌ Error fetching current semester:', semesterError);
          setError(
            'Failed to load semester information. Please try refreshing the page.',
          );
          return;
        }

        if (data && data.length > 0) {
          const semester = data[0].semester_year;
          console.log(`✅ Active semester found: ${semester}`);
          setCurrentYear(semester);
          setError(null); // Clear any previous errors
        } else {
          console.warn('⚠️ No active semester found');
          setError(
            'No active semester is currently set. Please go to Admin → Semester Management to create and activate a semester before managing classes.',
          );
          // Don't load class data if no active semester
          return;
        }
      } catch (err) {
        console.error('💥 Unexpected error fetching current year:', err);
        setError(
          'An unexpected error occurred while loading semester information. Please try refreshing the page or contact support.',
        );
      }
    };

    if (!currentYear) {
      fetchCurrentYear();
    }
  }, [currentYear, semesterOps]);

  // Fetch teacher names and combine with class data - only if semester is active
  useEffect(() => {
    const fetchTeacherNames = async () => {
      // Don't load class data if there's no active semester or there's a semester error
      if (!currentYear || error) {
        console.log(
          '⏭️ Skipping class data loading - no active semester or error present',
        );
        setClasses([]);
        return;
      }

      try {
        setIsEnriching(true);
        console.log(`🏫 Loading class data for semester: ${currentYear}`);

        // Filter classes to only show those for the current semester
        const currentSemesterClasses = classTableData.filter(
          (c: ClassProfile) =>
            c.semester_year === (year || currentYear) && !(c as any).deleted,
        );

        console.log(
          `Found ${currentSemesterClasses.length} classes for current semester`,
        );

        if (currentSemesterClasses.length === 0) {
          setClasses([]);
          return;
        }

        // We should show all the classes regardless of teacher assignment
        const teacherNames = await Promise.all(
          currentSemesterClasses.map((c: ClassProfile) =>
            userOps.getTeacherNameByTeacherToken(c.teacher_token),
          ),
        );

        const combined = teacherNames.map((teacherName, i) => {
          const teacher = teacherName.data?.[0];
          return teacher
            ? { ...currentSemesterClasses[i], teacher_name: teacher.full_name }
            : { ...currentSemesterClasses[i], teacher_name: 'Unassigned' };
        });
        console.log(
          `✅ Successfully loaded ${combined.length} classes with teacher info`,
        );
        setClasses(combined);
      } catch (err) {
        console.error('💥 Error fetching class/teacher data:', err);
        setError(
          'Failed to load class information. Please try refreshing the page.',
        );
        setClasses([]);
      } finally {
        setIsEnriching(false);
      }
    };

    if (classTableData && classTableData.length > 0) {
      fetchTeacherNames();
    } else if (!error && currentYear) {
      // If we have current year but no class data, set empty array
      setClasses([]);
      setIsEnriching(false);
    }
  }, [classTableData, userOps, currentYear, error, year]);

  const displayYear = year || currentYear;
  const isViewingCurrentYear = () => year === currentYear || year === undefined;

  // Filter classes for current year
  const currentClasses = useMemo(
    () => classes.filter((c) => c.semester_year === displayYear),
    [classes, displayYear],
  );

  const sortedClasses = useMemo(() => {
    const parseSortKey = (name: string) => {
      const matches = name.match(/\d+/g) || [];
      const grade = matches[0] ? parseInt(matches[0], 10) : 0;
      const room = matches[1] ? parseInt(matches[1], 10) : 0;
      return { grade, room };
    };

    return [...currentClasses].sort((a, b) => {
      const aKey = parseSortKey(a.class_name);
      const bKey = parseSortKey(b.class_name);

      if (aKey.grade !== bKey.grade) {
        return aKey.grade - bKey.grade;
      }

      if (aKey.room !== bKey.room) {
        return aKey.room - bKey.room;
      }

      return (a.class_name || '').localeCompare(b.class_name || '');
    });
  }, [currentClasses]);

  // Calculate stats
  const totalClasses = currentClasses.length;
  const unassignedClasses = currentClasses.filter(
    (c) => !c.teacher_token || c.teacher_name === 'Unassigned',
  ).length;
  const assignedTeacherCount = new Set(
    currentClasses
      .filter((c) => c.teacher_token && c.teacher_name !== 'Unassigned')
      .map((c) => c.teacher_token),
  ).size;
  const hasFilters = !!search.trim() || assignmentFilter !== 'all';
  const clearFilters = () => {
    setSearch('');
    setAssignmentFilter('all');
  };
  const visibleClasses = sortedClasses.filter(
    (c) =>
      (assignmentFilter === 'all' ||
        !c.teacher_token ||
        c.teacher_name === 'Unassigned') &&
      `${c.class_name} ${c.teacher_name || ''}`
        .toLowerCase()
        .includes(search.trim().toLowerCase()),
  );
  const isLoading = !error && (!currentYear || isTableLoading || isEnriching);
  const totalStudents = currentClasses.reduce(
    (sum, c) => sum + (c.number_of_students || 0),
    0,
  );

  // Event handlers
  const handleDeleteClick = (classItem: EnhancedClassTeacherProfile) => {
    setDeleteId(classItem.class_token);
    setIsDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    setIsDeleting(true);

    try {
      await classOps.softDeleteById(TABLE_NAMES.CLASS, 'class_token', deleteId);
      setClasses((prev) => prev.filter((c) => c.class_token !== deleteId));
      setIsDeleteOpen(false);
    } catch (err) {
      console.error('Delete failed:', err);
      setError('Failed to delete class');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleAddNewClass = () => {
    navigate('/admin/class/new');
  };

  const handleRowClick = (classItem: EnhancedClassTeacherProfile) => {
    navigate(`/admin/${displayYear}/class/${classItem.class_token}/students`);
  };

  const handleEditClass = (classItem: EnhancedClassTeacherProfile) => {
    navigate(`/admin/class/${classItem.class_token}/edit`);
  };

  // Define table columns
  const columns: TableColumn<EnhancedClassTeacherProfile>[] = [
    {
      key: 'class_name',
      header: 'Class Name',
      sortable: true,
      render: (classItem: EnhancedClassTeacherProfile, value: string) => (
        <Box>
          <Text fontSize="sm" fontWeight="semibold">
            {value}
          </Text>
          <Text fontSize="xs" color={muted} mt={0.5}>
            {classItem.grade ? `Grade ${classItem.grade}` : 'Grade not set'}
          </Text>
        </Box>
      ),
    },
    {
      key: 'teacher_name',
      header: 'Teacher',
      sortable: true,
      render: (classItem: EnhancedClassTeacherProfile, value: string) =>
        !classItem.teacher_token || value === 'Unassigned' ? (
          <Badge
            colorScheme="orange"
            variant="subtle"
            borderRadius="sm"
            textTransform="none"
          >
            Needs teacher
          </Badge>
        ) : (
          <Text fontSize="sm">{value || 'Not assigned'}</Text>
        ),
    },
    {
      key: 'number_of_students',
      header: 'Students',
      sortable: true,
      render: (classItem: EnhancedClassTeacherProfile, value: number) => (
        <HStack spacing={2}>
          <FiUsers size={16} />
          <span>{value || 0}</span>
        </HStack>
      ),
    },
  ];

  if (classTableDataError) {
    return (
      <CrudPageLayout
        compact
        title="Class Management"
        table={{
          data: [],
          columns: [],
          error: new Error('Something went wrong. Please try again later.'),
        }}
      />
    );
  }

  return (
    <>
      <CrudPageLayout
        compact
        title="Classes"
        subtitle="Manage classes, teachers, and student enrollment."
        headerBadge={
          displayYear
            ? {
                text: `Semester ${displayYear}`,
                colorScheme: 'blue',
                variant: 'subtle',
              }
            : undefined
        }
        breadcrumb={{
          items: [
            { label: 'Admin', href: '/admin' },
            { label: 'Classes', isCurrent: true },
          ],
        }}
        createButton={
          isViewingCurrentYear()
            ? {
                label: 'Add New Class',
                onClick: handleAddNewClass,
              }
            : undefined
        }
        headerActions={
          !isViewingCurrentYear() && (
            <Badge colorScheme="gray" borderRadius="sm" px={2} py={1}>
              Historical semester
            </Badge>
          )
        }
        table={{
          data: visibleClasses,
          columns,
          loading: isLoading,
          error: error ? new Error(error) : undefined,
          clickableRows: true,
          onRowClick: handleRowClick,
          actions: isViewingCurrentYear()
            ? [
                {
                  label: 'Edit',
                  icon: <FiEdit />,
                  onClick: handleEditClass,
                  variant: 'secondary',
                },
                {
                  label: 'Delete',
                  icon: <FiTrash2 />,
                  onClick: handleDeleteClick,
                  variant: 'danger',
                },
              ]
            : [],
          emptyState: (
            <VStack spacing={2} py={8} color={muted} textAlign="center">
              <FiBookOpen size={28} />
              <Text fontWeight="600">
                {hasFilters
                  ? 'No matching classes'
                  : `No classes for ${displayYear}`}
              </Text>
              <Text fontSize="sm">
                {hasFilters
                  ? 'Try another name or clear the assignment filter.'
                  : 'Classes will appear here once created for this semester.'}
              </Text>
              {hasFilters && (
                <Button size="sm" variant="ghost" onClick={clearFilters}>
                  Clear filters
                </Button>
              )}
            </VStack>
          ),
        }}
      >
        <AdminSummary
          loading={isLoading}
          items={[
            { label: 'Classes', value: totalClasses },
            { label: 'Students', value: totalStudents },
            { label: 'Assigned teachers', value: assignedTeacherCount },
            { label: 'Classes needing a teacher', value: unassignedClasses },
          ]}
        />
        <VStack spacing={2} align="stretch">
          <Stack
            direction={{ base: 'column', md: 'row' }}
            justify="space-between"
            align={{ base: 'stretch', md: 'center' }}
            spacing={3}
          >
            <ButtonGroup
              size="sm"
              isAttached
              variant="outline"
              aria-label="Filter by teacher assignment"
            >
              <Button
                onClick={() => setAssignmentFilter('all')}
                isDisabled={isLoading}
                aria-pressed={assignmentFilter === 'all'}
                colorScheme={assignmentFilter === 'all' ? 'blue' : 'gray'}
                variant={assignmentFilter === 'all' ? 'solid' : 'outline'}
              >
                All classes ({totalClasses})
              </Button>
              <Button
                onClick={() => setAssignmentFilter('unassigned')}
                isDisabled={isLoading}
                aria-pressed={assignmentFilter === 'unassigned'}
                colorScheme={
                  assignmentFilter === 'unassigned' ? 'blue' : 'gray'
                }
                variant={
                  assignmentFilter === 'unassigned' ? 'solid' : 'outline'
                }
              >
                Needs teacher ({unassignedClasses})
              </Button>
            </ButtonGroup>
            <InputGroup size="sm" maxW={{ md: '360px' }}>
              <InputLeftElement pointerEvents="none">
                <FiSearch />
              </InputLeftElement>
              <Input
                type="search"
                aria-label="Search classes"
                placeholder="Search class or teacher"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                borderRadius="md"
              />
            </InputGroup>
          </Stack>
          <HStack justify="space-between" spacing={3} flexWrap="wrap">
            <Text fontSize="xs" color={muted} role="status">
              {isLoading
                ? 'Loading classes…'
                : `${visibleClasses.length} of ${totalClasses} classes`}
            </Text>
            {hasFilters ? (
              <Button size="xs" variant="link" onClick={clearFilters}>
                Reset filters
              </Button>
            ) : (
              <Text fontSize="xs" color={muted}>
                Select a class to view its students.
              </Text>
            )}
          </HStack>
        </VStack>
      </CrudPageLayout>

      <ConfirmModal
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        type="danger"
        title="Delete Class"
        message="Are you sure you want to delete this class?"
        description="This will not delete students, but will remove the class organization. This action cannot be undone."
        onConfirm={handleConfirmDelete}
        confirmText="Delete Class"
        loading={isDeleting}
      />
    </>
  );
};
